import { useEffect, useMemo, useState } from 'react';
import useGameStore from '../store/gameStore';
import {
  CREDIT_RATING_META,
  DISTRICT_GROUPS,
  LOAN_STRUCTURE_LABEL,
  INVESTMENT_TIER_META,
  creditRatingOf,
} from '../types/gameTypes';
import type { InvestmentTier, Loan, LoanStructure, MarketPhase, Player, PropertyTile } from '../types/gameTypes';
import { money, percent } from '../lib/format';
import { tileName as localizeTile, useT } from '../i18n';

const panel = 'rounded-xl border border-slate-700/60 bg-slate-900/60 p-3';
const chip = 'rounded-full border border-slate-600/60 bg-slate-800/80 px-2 py-0.5 text-[10px] font-bold';
const STRUCTURES: LoanStructure[] = ['ANNUITY', 'INTEREST_ONLY', 'BALLOON'];
const TIERS: InvestmentTier[] = ['SAVINGS', 'REIT', 'VENTURE'];

/** One labelled money line in the balance sheet / cash-flow tables. */
const MoneyRow = ({ label, value, tone = 'neutral', hint }: { label: string; value: number; tone?: 'neutral' | 'good' | 'bad'; hint?: string }) => (
  <div className="flex items-center justify-between gap-2 border-b border-slate-800/60 py-1 last:border-0" title={hint}>
    <span className="min-w-0 truncate text-slate-400">{label}</span>
    <span className={`shrink-0 font-mono font-bold ${tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-rose-300' : 'text-slate-200'}`}>
      {money(value)}
    </span>
  </div>
);

const SubTotal = ({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'good' | 'bad' }) => (
  <div className="mt-1 flex items-center justify-between gap-2 border-t border-slate-600/60 pt-1.5">
    <span className="text-[11px] font-black uppercase tracking-widest text-slate-300">{label}</span>
    <span className={`font-mono text-sm font-black ${tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-rose-300' : 'text-slate-100'}`}>
      {money(value)}
    </span>
  </div>
);

/** Asset / liability valuation helpers (mirror the store maths so the dossier matches the HUD). */
const equityValueOf = (board: PropertyTile[], playerId: string, phase: MarketPhase): number =>
  board.reduce((sum, tile) => {
    const shares = tile.shares[playerId] ?? 0;
    if (shares <= 0) return sum;
    const valuation = tile.valuation > 0 ? tile.valuation : tile.baseCost;
    const phaseMul = phase === 'BOOM' ? 1.25 : phase === 'CRISIS' ? 0.7 : 1;
    return sum + (shares / Math.max(1, tile.totalShares)) * Math.round(valuation * phaseMul * (1 + tile.upgradeLevel * 0.35));
  }, 0);

/**
 * 📊 Player Financial Dossier — full financial health of any rival (or yourself):
 * balance sheet + credit rating, lifetime cash-flow telemetry and a colour-coded audit log.
 * Opened by clicking a card in the board-centre Live Ledger.
 */
const PlayerDossierModal = () => {
  const store = useGameStore();
  const t = useT();
  const { inspectedPlayerId, players, board, market, loans, investments } = store;
  const [tab, setTab] = useState<'BALANCE' | 'CASHFLOW' | 'AUDIT'>('BALANCE');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') store.setInspectedPlayer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);

  const player = useMemo(() => players.find((p) => p.id === inspectedPlayerId), [players, inspectedPlayerId]);

  if (!inspectedPlayerId || !player) return null;

  const rating = creditRatingOf(player.creditScore);
  const ratingMeta = CREDIT_RATING_META[rating];
  const stats = player.financialStats;
  const myLoans = loans.filter((loan) => loan.playerId === player.id);
  const myInvestments = investments.filter((entry) => entry.playerId === player.id);

  // ---- Balance sheet -------------------------------------------------------------
  const cash = player.cash;
  const equity = Math.round(equityValueOf(board, player.id, market.phase));
  const funds = Math.round(myInvestments.reduce((sum, entry) => sum + entry.value, 0));
  const totalAssets = cash + equity + funds;
  const loanDebt = Math.round(myLoans.reduce((sum, loan) => sum + loan.outstanding, 0));
  const formalDebt = Math.round(player.debt);
  const totalLiabilities = loanDebt + formalDebt;
  const ceiling = store.debtCeilingOf(player.id);
  const netWorth = totalAssets - totalLiabilities;

  // ---- Cash flow telemetry -------------------------------------------------------
  const inflows = [
    { label: `💵 ${t('dossier.rentCollected')}`, value: stats.rentCollected },
    { label: `📈 ${t('dossier.shareSaleProceeds')}`, value: stats.shareSaleProceeds },
    { label: `🏦 ${t('dossier.investmentYield')}`, value: stats.investmentYield },
    { label: `🚇 ${t('dossier.transitDividends')}`, value: stats.transitDividends },
    { label: `💰 ${t('dossier.loanDraws')}`, value: stats.loanDraws },
  ];
  const outflows = [
    { label: `💸 ${t('dossier.rentPaid')}`, value: stats.rentPaid },
    { label: `🏗️ ${t('dossier.sharePurchases')}`, value: stats.sharePurchases },
    { label: `🏗️ ${t('dossier.buildingUpgrades')}`, value: stats.buildingUpgrades },
    { label: `📉 ${t('dossier.interestPaid')}`, value: stats.interestPaid },
    { label: `🛡️ ${t('dossier.insurancePremiums')}`, value: stats.insurancePremiums },
    { label: `⚖️ ${t('dossier.bankFees')}`, value: stats.bankFees },
    { label: `🚨 ${t('dossier.foreclosureSeized')}`, value: stats.foreclosureSeized },
  ];
  const totalIn = inflows.reduce((sum, entry) => sum + entry.value, 0);
  const totalOut = outflows.reduce((sum, entry) => sum + entry.value, 0);
  const netFlow = totalIn - totalOut;

  const holdingRows = board
    .map((tile) => ({ tile, shares: tile.shares[player.id] ?? 0 }))
    .filter((row) => row.shares > 0)
    .sort((a, b) => b.shares / Math.max(1, b.tile.totalShares) - a.shares / Math.max(1, a.tile.totalShares));

  const loanTotalByStructure = (structure: LoanStructure) =>
    myLoans.filter((loan) => loan.structure === structure).reduce((sum, loan) => sum + loan.outstanding, 0);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm"
      onClick={() => store.setInspectedPlayer(null)}
    >
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border-2 border-slate-700 bg-slate-900 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-700/60 bg-gradient-to-r from-slate-800/80 to-slate-900 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-white/20 text-sm font-black" style={{ backgroundColor: player.color }}>
              {player.name.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">{t('dossier.title')}</p>
              <h2 className="truncate text-lg font-black text-white">
                {player.name} {player.isAI ? '🤖' : '🧑'}
                {player.isBankrupt && <span className="ml-2 rounded bg-rose-600/30 px-1.5 py-0.5 text-[10px] font-black text-rose-200">{t('dossier.bankrupt')}</span>}
              </h2>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="rounded-full border px-2 py-0.5 text-[10px] font-black" style={{ borderColor: `${ratingMeta.accent}88`, color: ratingMeta.accent, backgroundColor: `${ratingMeta.accent}1a` }}>
              {rating} · {player.creditScore}/100
            </span>
            <button type="button" onClick={() => store.setInspectedPlayer(null)} className="rounded-lg bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300 hover:bg-slate-700">
              {t('common.close')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {([
            ['BALANCE', t('dossier.tabBalance')],
            ['CASHFLOW', t('dossier.tabCashflow')],
            ['AUDIT', t('dossier.tabAudit')],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`min-h-[34px] rounded-xl px-3 text-[11px] font-extrabold transition ${
                tab === id ? 'bg-amber-400 text-slate-950' : 'border border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>


        {/* ---- Tab 1: Balance sheet & credit rating ---- */}
        {tab === 'BALANCE' && (
          <div className="space-y-3 p-4 text-[11px]">
            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">🟢 {t('dossier.assets')}</p>
              <MoneyRow label={t('dossier.cash')} value={cash} tone={cash < 0 ? 'bad' : 'neutral'} />
              <MoneyRow label={t('dossier.equity')} value={equity} />
              <MoneyRow label={t('dossier.funds')} value={funds} hint={`${t('dossier.costBasis')}: ${money(myInvestments.reduce((s, e) => s + e.principal, 0))}`} />
              <SubTotal label={t('dossier.totalAssets')} value={totalAssets} tone="good" />
            </div>

            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">🔴 {t('dossier.liabilities')}</p>
              {STRUCTURES.map((structure) => (
                <MoneyRow
                  key={structure}
                  label={LOAN_STRUCTURE_LABEL[structure].label}
                  value={loanTotalByStructure(structure)}
                  tone={loanTotalByStructure(structure) > 0 ? 'bad' : 'neutral'}
                />
              ))}
              <MoneyRow label={t('dossier.informalDebt')} value={formalDebt} tone={formalDebt > 0 ? 'bad' : 'neutral'} />
              <SubTotal label={t('dossier.totalLiabilities')} value={totalLiabilities} tone="bad" />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-1 text-[10px] text-slate-400">
                <span>{t('dossier.ceiling')}: <b className="text-slate-200">{money(ceiling)}</b></span>
                <span>{t('dossier.used')}: <b className="text-rose-200">{money(totalLiabilities)}</b></span>
                <span>{t('dossier.free')}: <b className="text-emerald-200">{money(Math.max(0, ceiling - totalLiabilities))}</b></span>
              </div>
            </div>

            <div className={`${panel} flex flex-wrap items-center justify-between gap-2`}>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('dossier.creditRating')}</p>
                <p className="mt-0.5 flex items-center gap-2">
                  <span className="rounded-full border px-2 py-0.5 text-[11px] font-black" style={{ borderColor: `${ratingMeta.accent}88`, color: ratingMeta.accent, backgroundColor: `${ratingMeta.accent}1a` }}>
                    {ratingMeta.label}
                  </span>
                  <span className="text-slate-400">{ratingMeta.blurb}</span>
                </p>
                {ratingMeta.markup > 0 && (
                  <p className="mt-0.5 text-[10px] font-bold text-orange-300">
                    {t('dossier.markupNote', { pct: percent(ratingMeta.markup * 100, 0) })}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('dossier.netWorth')}</p>
                <p className={`font-mono text-xl font-black ${netWorth >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{money(netWorth)}</p>
              </div>
            </div>

            {myLoans.length > 0 && (
              <div className={panel}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('dossier.activeLoans')}</p>
                <div className="mt-1.5 space-y-1">
                  {myLoans.map((loan: Loan) => (
                    <div key={loan.id} className="flex flex-wrap items-center justify-between gap-1 rounded-lg border border-slate-700/60 bg-slate-950/50 px-2 py-1">
                      <span className="font-bold text-slate-200">{LOAN_STRUCTURE_LABEL[loan.structure].label}</span>
                      <span className="font-mono text-slate-300">
                        {money(loan.outstanding)} @ {percent(loan.ratePerRound * 100, 1)}/{t('common.round')} · {t('desk.matures', { round: loan.maturityRound })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {holdingRows.length > 0 && (
              <div className={panel}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('dossier.holdings')}</p>
                <div className="mt-1 max-h-40 space-y-0.5 overflow-y-auto pr-1 scrollbar-thin">
                  {holdingRows.map(({ tile, shares }) => (
                    <div key={tile.id} className="flex items-center justify-between gap-1 text-[10px]">
                      <span className="min-w-0 truncate text-slate-300">{localizeTile(tile.name, t)}</span>
                      <span className="shrink-0 font-mono text-slate-400">
                        {shares}/{tile.totalShares} ({percent((shares / Math.max(1, tile.totalShares)) * 100, 0)})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {/* ---- Tab 2: Cash flow & financial telemetry ---- */}
        {tab === 'CASHFLOW' && (
          <div className="space-y-3 p-4 text-[11px]">
            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">🟢 {t('dossier.inflows')}</p>
              {inflows.map((row) => (
                <MoneyRow key={row.label} label={row.label} value={row.value} tone={row.value > 0 ? 'good' : 'neutral'} />
              ))}
              <SubTotal label={t('dossier.totalInflows')} value={totalIn} tone="good" />
            </div>

            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">🔴 {t('dossier.outflows')}</p>
              {outflows.map((row) => (
                <MoneyRow key={row.label} label={row.label} value={row.value} tone={row.value > 0 ? 'bad' : 'neutral'} />
              ))}
              <SubTotal label={t('dossier.totalOutflows')} value={totalOut} tone="bad" />
            </div>

            <div className={`${panel} flex flex-wrap items-center justify-between gap-2`}>
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-300">{t('dossier.netFlow')}</span>
              <span className={`font-mono text-lg font-black ${netFlow >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                {netFlow >= 0 ? '+' : '−'}{money(Math.abs(netFlow))}
              </span>
            </div>

            <div className={panel}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">🏦 {t('dossier.funds')}</p>
              <div className="mt-1.5 space-y-1">
                {TIERS.map((tier) => {
                  const entry = myInvestments.find((item) => item.tier === tier);
                  return (
                    <div key={tier} className="flex flex-wrap items-center justify-between gap-1">
                      <span className="text-slate-300">{INVESTMENT_TIER_META[tier].name}</span>
                      <span className="font-mono text-slate-300">
                        {money(entry?.value ?? 0)}
                        {entry && <span className={entry.value - entry.principal >= 0 ? ' text-emerald-300' : ' text-rose-300'}> ({entry.value - entry.principal >= 0 ? '+' : '−'}{money(Math.abs(entry.value - entry.principal))})</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-[10px] italic text-slate-500">{t('dossier.cashflowNote')}</p>
          </div>
        )}


        {/* ---- Tab 3: Audit log ---- */}
        {tab === 'AUDIT' && (
          <div className="space-y-3 p-4 text-[11px]">
            {player.ledger.length === 0 ? (
              <p className="text-slate-400">{t('dossier.auditEmpty')}</p>
            ) : (
              <div className="max-h-[52vh] space-y-1 overflow-y-auto pr-1 scrollbar-thin">
                {player.ledger.slice(0, 20).map((entry) => (
                  <div
                    key={entry.id}
                    className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-2 py-1 ${
                      entry.kind === 'INCOME' ? 'border-emerald-600/40 bg-emerald-500/5' : 'border-rose-600/40 bg-rose-500/5'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate text-slate-300">
                      <span className="mr-1.5 font-mono text-[10px] text-slate-500">[R{entry.round}]</span>
                      <span className="mr-1.5 rounded bg-slate-800/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                        {entry.category}
                      </span>
                      {entry.label}
                    </span>
                    <span className={`shrink-0 font-mono font-black ${entry.kind === 'INCOME' ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {entry.kind === 'INCOME' ? '+' : '−'}{money(entry.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDossierModal;

